/**
 * Main App Component
 * Orchestrates game systems and renders views
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { useCurrentTab, useGameStore } from '@/state';
import { saveManager, distributeOfflineFish } from '@/systems';
import { eventBus } from '@/services/EventBus';
import { GameEvent } from '@/types';
import { awardXP } from '@/types';
import { DEBUG_ENABLED, DEBUG_SHORTCUT } from '@/config/debug';

// Layout
import { Header } from '@/components/layout/Header';
import { TabNavigation, TAB_IDS } from '@/components/layout/TabNavigation';
import { useSwipeNavigation } from '@/hooks/useSwipeNavigation';

// Custom hooks (extracted from App.tsx)
import { useSoundInitialization } from '@/hooks/useSoundInitialization';
import { useGameInitialization } from '@/hooks/useGameInitialization';
import { useEventSubscriptions } from '@/hooks/useEventSubscriptions';

// Views
import { FishingView } from '@/components/fishing/FishingView';
import { InventoryView } from '@/components/inventory/InventoryView';
import { OutfitterView } from '@/components/shops/OutfitterView';
import { MarketView } from '@/components/shops/MarketView';
import { BuddiesView } from '@/components/buddies/BuddiesView';
import { StatsView } from '@/components/stats/StatsView';
import { MilestonesView } from '@/components/milestones';

// Quest Dock
import { QuestDock } from '@/components/quests';

// Offline progress
import { WelcomeBackModal } from '@/components/offline';
import type { OfflineReport } from '@/systems/offline';

// Visual feedback
import { ScreenFlash } from '@/components/common/ScreenFlash';

// Debug
import { DebugMenu } from '@/components/debug/DebugMenu';

export function App() {
  const currentTab = useCurrentTab();
  const [debugMenuVisible, setDebugMenuVisible] = useState(false);
  const [offlineReport, setOfflineReport] = useState<OfflineReport | null>(null);
  const mainRef = useRef<HTMLElement>(null);

  // Track slide direction for tab transition animation
  const prevTabIndexRef = useRef(TAB_IDS.indexOf(currentTab));
  const currentTabIndex = TAB_IDS.indexOf(currentTab);
  const slideDirection =
    currentTabIndex > prevTabIndexRef.current ? 'right' :
    currentTabIndex < prevTabIndexRef.current ? 'left' : null;

  useEffect(() => {
    prevTabIndexRef.current = currentTabIndex;
  }, [currentTabIndex]);

  // Swipe navigation between tabs
  const handleSwipeLeft = useCallback(() => {
    const { currentTab } = useGameStore.getState();
    const idx = TAB_IDS.indexOf(currentTab);
    if (idx < TAB_IDS.length - 1) {
      useGameStore.getState().setCurrentTab(TAB_IDS[idx + 1]);
    }
  }, []);

  const handleSwipeRight = useCallback(() => {
    const { currentTab } = useGameStore.getState();
    const idx = TAB_IDS.indexOf(currentTab);
    if (idx > 0) {
      useGameStore.getState().setCurrentTab(TAB_IDS[idx - 1]);
    }
  }, []);

  useSwipeNavigation(mainRef, {
    onSwipeLeft: handleSwipeLeft,
    onSwipeRight: handleSwipeRight,
  });

  // Custom hooks replace the 3 massive useEffect blocks
  useSoundInitialization();
  useGameInitialization(setOfflineReport, setDebugMenuVisible);
  useEventSubscriptions();

  // Debug keyboard shortcut
  useEffect(() => {
    if (!DEBUG_ENABLED) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const ctrlOrCmd = e.ctrlKey || e.metaKey;
      const matchesShortcut =
        e.key.toUpperCase() === DEBUG_SHORTCUT.key &&
        ctrlOrCmd === DEBUG_SHORTCUT.ctrl &&
        e.shiftKey === DEBUG_SHORTCUT.shift &&
        e.altKey === DEBUG_SHORTCUT.alt;

      if (matchesShortcut) {
        e.preventDefault();
        setDebugMenuVisible((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle offline progress collection
  const handleOfflineCollect = useCallback(() => {
    if (!offlineReport) return;

    // Add fish to inventory (distributed across species by rarity weight)
    if (offlineReport.fishToInventory > 0) {
      const zoneId = useGameStore.getState().playerState.currentZone;
      const stacks = distributeOfflineFish(offlineReport.fishToInventory, zoneId);

      useGameStore.setState((draft) => {
        const inv = draft.playerState.inventory;
        for (const stack of stacks) {
          const existing = inv.stacks.find(
            (s) => s.definitionId === stack.definitionId && s.quality === stack.quality
          );
          if (existing) {
            existing.count += stack.count;
          } else {
            inv.stacks.push({
              definitionId: stack.definitionId,
              quality: stack.quality,
              count: stack.count,
            });
          }
          inv.currentCount += stack.count;
        }
      });
    }

    // Add gold from hauler sales
    if (offlineReport.goldEarned > 0) {
      useGameStore.getState().addCurrency(offlineReport.goldEarned);
    }

    // Award XP
    if (offlineReport.xpEarned > 0) {
      useGameStore.setState((draft) => {
        const newProgression = awardXP(
          draft.playerState.progression,
          offlineReport.xpEarned
        );
        draft.playerState.progression = newProgression;
      });
    }

    // Emit event
    eventBus.emit(GameEvent.OFFLINE_PROGRESS_CALCULATED, {
      offlineDurationMs: offlineReport.offlineDurationMs,
      fishCaught: offlineReport.fishCaught,
      fishToInventory: offlineReport.fishToInventory,
      fishSold: offlineReport.fishSold,
      goldEarned: offlineReport.goldEarned,
      buddyCatches: offlineReport.buddyCatches,
      haulerTrips: offlineReport.haulerTrips,
    });

    // Close modal and save
    setOfflineReport(null);
    saveManager.save();
  }, [offlineReport]);

  // Render current view
  const renderView = () => {
    switch (currentTab) {
      case 'fishing':
        return <FishingView />;
      case 'inventory':
        return <InventoryView />;
      case 'outfitters':
        return <OutfitterView />;
      case 'markets':
        return <MarketView />;
      case 'buddies':
        return <BuddiesView />;
      case 'stats':
        return <StatsView />;
      case 'milestones':
        return <MilestonesView />;
      default:
        return <FishingView />;
    }
  };

  return (
    <div className="w-full min-h-0 text-white overflow-hidden safe-area-top" style={{ height: '100dvh' }}>
      {/* Content channel — constrains app to max-w-7xl with subtle borders on wide screens */}
      <div className="h-full max-w-7xl mx-auto flex flex-col bg-surface-base border-x border-gray-700/20 shadow-[0_0_40px_rgba(0,0,0,0.5)]">
        <Header onDebugToggle={() => setDebugMenuVisible((prev) => !prev)} />
        <main ref={mainRef} className="flex-1 flex flex-col overflow-hidden">
          <div
            key={currentTab}
            className={`flex-1 flex flex-col overflow-hidden ${
              slideDirection === 'right' ? 'tab-slide-right' :
              slideDirection === 'left' ? 'tab-slide-left' : ''
            }`}
          >
            {renderView()}
          </div>
        </main>
        {/* Quest Dock - Static dock above tab bar */}
        <QuestDock />
        <TabNavigation />
      </div>
      <ScreenFlash />
      <Toaster position="top-center" />

      {/* Offline Progress Modal */}
      {offlineReport && (
        <WelcomeBackModal
          report={offlineReport}
          onCollect={handleOfflineCollect}
        />
      )}

      {/* Debug Menu (only if DEBUG_ENABLED) */}
      {DEBUG_ENABLED && (
        <DebugMenu
          visible={debugMenuVisible}
          onClose={() => setDebugMenuVisible(false)}
        />
      )}
    </div>
  );
}
